import React, { FC, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormikContext } from 'formik';
import styled from 'styled-components';
import { AnalyticsProps, Label, useAnalytics } from '@inplayer-org/inplayer-ui';

// hooks
import useRestrictionRules from 'restrictions/roles/hooks/useRestrictionRules';

// constants
import Rules from 'restrictions/roles/constants/rules';

// actions
import { loadResource } from 'ducks/merchantDucks/items/actions';

// selectors
import { getLiveLikeResources } from 'ducks/merchantDucks/items/selectors';

// styled components
import { BlueTypography } from 'components/SharedStyledElements';

// components
import CommonIcon from 'components/CommonIcon';
import MultipageDropdown, { Option } from 'components/MultipageDropdown';
import { useUncontrolledSwitch } from 'components/Switcher';

// own components
import BackButton from '../../components/BackButton';

// own various
import { SwitchableState, TwoRowGrid } from '../Field';
import { FormValues, TOP_FIELD_NAME } from '../../LiveLikeChatForm';

const HeadingTypography = styled(BlueTypography)`
  margin-bottom: 2em;
`;

const DropdownContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: 0.75em;
  margin: 0.5em 0;

  // move the Edit icon a bit down from its default relative position
  span:nth-child(2) {
    position: relative;
    top: 0.3em;
  }
`;

const CenteredDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 1em 0;
`;

const UnderlinedLink = styled.a<AnalyticsProps>`
  text-decoration: underline;
`;

const defaultOption: Option = {
  label: 'Select chat',
  isDisabled: true,
};

interface Props {
  handleBackToWidgets: (resetForm?: () => void) => void;
}

const SelectExistingChatroom: FC<Props> = ({ handleBackToWidgets }) => {
  const dispatch = useDispatch();

  const { switchTo, stateSwitcher } = useUncontrolledSwitch();

  const { track } = useAnalytics();

  const {
    values: { [TOP_FIELD_NAME]: currentChatroom },
    resetForm,
  } = useFormikContext<FormValues>();

  const { hasRule } = useRestrictionRules();
  const isReadOnly = hasRule(Rules.READONLY_ASSETS_AND_PACKAGES);
  const isSelectionValid = !!currentChatroom.id;

  const fetchChatrooms = useCallback((pageNumber = 1) => {
    dispatch(loadResource('chatrooms', { page: pageNumber }));
  }, []);

  const {
    chatRooms: { collection: chatRooms, total, isFetching },
  } = useSelector(getLiveLikeResources);

  const options = useMemo(
    () =>
      (isSelectionValid ? [currentChatroom, ...chatRooms] : chatRooms).map(
        (room): Option => ({
          label: room.title,
          value: room.id,
          data: { ...room },
          tag: room.title,
          isCurrent: room === currentChatroom,
        })
      ),
    [currentChatroom, chatRooms]
  );

  const handleGoBack = () => {
    handleBackToWidgets(resetForm);
  };

  const goToNewChatroomSection = () => {
    track({
      event: 'click',
      type: 'link',
      tag: 'link_create_new_chat',
    });

    switchTo(SwitchableState.CREATE_NEW_CHATROOM);
  };

  return (
    <TwoRowGrid>
      <BackButton onClick={handleGoBack}>Back</BackButton>
      <div>
        <HeadingTypography variant="h6">Select chat</HeadingTypography>
        <Label>Existing chat</Label>
        <DropdownContainer>
          <MultipageDropdown
            tag="dropdown_chatroom"
            name={TOP_FIELD_NAME}
            defaultOption={defaultOption}
            options={options}
            totalItems={total}
            isLoading={isFetching}
            onPageChange={fetchChatrooms}
            isDisabled={isReadOnly}
          />
          <CommonIcon
            tag="icon_button_edit_channel"
            name="edit"
            onClick={stateSwitcher(SwitchableState.EDIT_EXISTING_CHATROOM)}
            isIconDisabled={!isSelectionValid || isReadOnly}
          />
        </DropdownContainer>
        {!isReadOnly && (
          <CenteredDiv>
            or
            <UnderlinedLink onClick={goToNewChatroomSection}>Create new chat</UnderlinedLink>
          </CenteredDiv>
        )}
      </div>
    </TwoRowGrid>
  );
};

export default SelectExistingChatroom;
